import { z } from 'zod';

/**
 * Custom Zod schema for Prisma Decimal values
 * Handles both Prisma.Decimal objects and regular numbers
 */
export const decimalSchema = z.preprocess(
  // Handle conversion in preprocessing step
  (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') return val;
    // Handle Decimal.js object from Prisma
    if (typeof val === 'object' && val !== null && 'toNumber' in val) {
      return typeof val.toNumber === 'function' 
        ? val.toNumber() 
        : Number(val.toString());
    }
    return Number(val.toString());
  },
  // Then validate as number or null
  z.number().or(z.null())
);

/**
 * Optional decimal schema that allows null/undefined values
 */
export const optionalDecimalSchema = decimalSchema.optional();

/**
 * Date-only (`@db.Date`) columns.
 *
 * Prisma types these as `DateTime`, but the column carries no time component and
 * the value reaches a client as a JSON string (midnight UTC), never a `Date`.
 * Declaring such a field `z.date()` lets `new Date(value)` typecheck while
 * rendering the previous day in any timezone west of Greenwich, which is exactly
 * how the deal close date shipped a day early.
 *
 * Typing it as the union it really is forces call sites through a date-only
 * helper (`parseDateOnlyValue` / `toDateOnlyString`) instead of raw `Date` math.
 * `Date` stays in the union because form state holds a real `Date` before send.
 */
export const dateOnlySchema = z.union([z.string(), z.date()]);

/**
 * Create a decimal schema with min/max validation
 */
export function createDecimalSchema(options?: { min?: number; max?: number }) {
  let schema = z.number();
  
  if (options?.min !== undefined) {
    schema = schema.min(options.min);
  }
  
  if (options?.max !== undefined) {
    schema = schema.max(options.max);
  }
  
  return z.preprocess(
    (val) => {
      if (val === null || val === undefined) return null;
      if (typeof val === 'number') return val;
      if (typeof val === 'object' && val !== null && 'toNumber' in val) {
        return typeof val.toNumber === 'function' 
          ? val.toNumber() 
          : Number(val.toString());
      }
      return Number(val.toString());
    },
    schema.or(z.null())
  );
} 